import type {
  UserDetails,
  AgencyList,
  JobsData,
} from 'src/types/referrer-portal';
import type {
  ReferralPrograms,
  ReferralSelectedQuestionnaire,
} from 'src/types/referral-v2';
import {isReferrerPortal} from 'src/components/referral-v2/common';
import type {JobDescription} from 'src/types/referral-external';
import type {Dispatch, ThunkAction} from 'src/reducers';
import {showHandledApiError} from 'src/action-creators/modal';
import {thunkify as flow} from 'src/utils/thunks';
import {key, fetching} from 'src/utils/redux';
import * as reduxApi from 'src/utils/redux-api-referrer-portal';
import {DEFAULT_API_ERROR} from 'src/utils/referrer-portal';

import {
  RECEIVE_USER_DETAILS,
  RECEIVE_USER_DETAILS_ERROR,
  RECEIVE_AGENCY,
  RECEIVE_AGENCY_ERROR,
} from 'src/action-creators/referrer-portal/authentication';


const defaultApiErrorMessage =
  DEFAULT_API_ERROR.non_field_errors?.[0]?.message ?? '';

export const RECEIVE_JOB_DESCRIPTION = 'receive/job-description';
export const RECEIVE_JOB_DESCRIPTION_ERROR = 'receive/job-description-error';

export const RECEIVE_PROGRAM_QUESTIONS = 'receive/program-questions';
export const RECEIVE_PROGRAM_QUESTIONS_ERROR =
  'receive/program-questions-error';

export const RECEIVE_REFERRAL_PROGRAMS = 'receive/referral-programs';
export const RECEIVE_REFERRAL_PROGRAMS_ERROR =
  'receive/referral-programs-error';

export const RECEIVE_JOBS = 'receive/jobs';
export const RECEIVE_JOBS_ERROR = 'receive/jobs-error';

export const RECEIVE_JOB_LOCATIONS = 'receive/job-locations';
export const RECEIVE_JOB_LOCATIONS_ERROR = 'receive/job-locations-error';

const formApiBaseUrl = (agencyId: string): string =>
  isReferrerPortal() ? `form/agency/${agencyId}` : 'form';

type JobDescriptionAction = {
  type: typeof RECEIVE_JOB_DESCRIPTION;
  payload: JobDescription;
};

type JobDescriptionErrorAction = {
  type: typeof RECEIVE_JOB_DESCRIPTION_ERROR;
  payload?: string | null | undefined;
};

type ProgramQuestionAction = {
  type: typeof RECEIVE_PROGRAM_QUESTIONS;
  payload: ReferralSelectedQuestionnaire[];
};

type ProgramQuestionErrorAction = {
  type: typeof RECEIVE_PROGRAM_QUESTIONS_ERROR;
  payload?: string | null | undefined;
};

type ReferralProgramAction = {
  type: typeof RECEIVE_REFERRAL_PROGRAMS;
  payload: ReferralPrograms[];
};

type ReferralProgramErrorAction = {
  type: typeof RECEIVE_REFERRAL_PROGRAMS_ERROR;
  payload?: string | null | undefined;
};

type JobsAction = {
  type: typeof RECEIVE_JOBS;
  payload: JobsData[];
};

type JobsErrorAction = {
  type: typeof RECEIVE_JOBS_ERROR;
  payload?: string | null | undefined;
};

type JobLocationAction = {
  type: typeof RECEIVE_JOB_LOCATIONS;
  payload: string[];
};

type JobLocationErrorAction = {
  type: typeof RECEIVE_JOB_LOCATIONS_ERROR;
  payload?: string | null | undefined;
};
type ClearReferralProgramsDataAction = {
  type: typeof RECEIVE_REFERRAL_PROGRAMS;
  payload: null;
};

export type UserFormsActions =
  | JobDescriptionAction
  | JobDescriptionErrorAction
  | ProgramQuestionAction
  | ProgramQuestionErrorAction
  | ReferralProgramAction
  | ReferralProgramErrorAction
  | JobsAction
  | JobsErrorAction
  | JobLocationAction
  | JobLocationErrorAction
  | ClearReferralProgramsDataAction;

export const clearReferralProgramsData =
  (): ClearReferralProgramsDataAction => {
    return {type: RECEIVE_REFERRAL_PROGRAMS, payload: null};
  };

export const getUser: (referralLinkId: string) => ThunkAction<void> = flow(
  key(() => `form/user`),
  fetching(),
)(
  (referralLinkId) => (dispatch: Dispatch) =>
    dispatch(reduxApi.get(`form/user?link_id=${referralLinkId}`)).then(
      (response: UserDetails) => {
        dispatch({type: RECEIVE_USER_DETAILS, payload: response});
      },
      (error) => {
        const errorMessage: string =
          error?.response?.errors && error.response.errors[0]
            ? error.response.errors[0]
            : defaultApiErrorMessage;
        if (errorMessage !== 'InvalidLinkID') {
          dispatch(showHandledApiError({json: error, text: errorMessage}));
        }
        dispatch({
          // @ts-expect-error - TS2769 - No overload matches this call.
          type: RECEIVE_USER_DETAILS_ERROR,
          // @ts-expect-error - TS2769 - No overload matches this call.
          payload: errorMessage,
        });
      },
    ),
);

export const getAllowedAgency: (referralLinkId: string) => ThunkAction<void> =
  flow(
    key(() => `form/user/agencies`),
    fetching(),
  )((referralLinkId) => (dispatch: Dispatch) => {
    return dispatch(
      reduxApi.get(`form/user/agencies?link_id=${referralLinkId}`),
    ).then(
      (response: AgencyList[]) => {
        dispatch({type: RECEIVE_AGENCY, payload: response});
      },
      (error) => {
        const errorMessage: string =
          error?.responseBody?.message || defaultApiErrorMessage;
        dispatch({
          // @ts-expect-error - TS2769 - No overload matches this call.
          type: RECEIVE_AGENCY_ERROR,
          // @ts-expect-error - TS2769 - No overload matches this call.
          payload: errorMessage,
        });
      },
    );
  });

export const getAgencyPrograms: (agencyId: string) => ThunkAction<void> = flow(
  key((agencyId: string) => `${formApiBaseUrl(agencyId)}/programs`),
  fetching(),
)((agencyId) => (dispatch: Dispatch) => {
  return dispatch(reduxApi.get(`${formApiBaseUrl(agencyId)}/programs`)).then(
    (response: ReferralPrograms[]) => {
      dispatch({
        type: RECEIVE_REFERRAL_PROGRAMS,
        payload: response,
      });
    },
    (error) => {
      const errorMessage: string =
        error?.responseBody?.message || defaultApiErrorMessage;
      dispatch({
        type: RECEIVE_REFERRAL_PROGRAMS_ERROR,
        payload: errorMessage,
      });
    },
  );
});

export const getProgramJobs: (
  agencyId: string,
  programId: string,
  query: {
    search_str: string;
    offset: number;
    limit: number;
    location: string;
  },
) => ThunkAction<void> = flow(
  key(
    (agencyId, programId) =>
      `${formApiBaseUrl(agencyId)}/programs/${programId}/jobs`,
  ),
  fetching(),
)((agencyId, programId, query) => (dispatch: Dispatch) => {
  const {search_str = '', offset = 0, limit = 0, location = ''} = query;
  const queryString = `?offset=${offset}&limit=${limit}&search_str=${search_str}&location=${location}`;

  return dispatch(
    reduxApi.get(
      `${formApiBaseUrl(agencyId)}/programs/${programId}/jobs${queryString}`,
    ),
  ).then(
    (response: JobsData[]) => {
      dispatch({
        type: RECEIVE_JOBS,
        payload: response,
      });
    },
    (error) => {
      const errorMessage: string =
        error?.responseBody?.message || defaultApiErrorMessage;
      dispatch({
        type: RECEIVE_JOBS_ERROR,
        payload: errorMessage,
      });
    },
  );
});

export const fetchJobDescription: (
  agencyId: string,
  jobId: string,
) => ThunkAction<void> = flow(
  key(
    (agencyId, jobId) =>
      `${formApiBaseUrl(agencyId)}/jobs/${jobId}/description`,
  ),
  fetching(),
)((agencyId, jobId) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.get(`${formApiBaseUrl(agencyId)}/jobs/${jobId}/description`),
  ).then(
    (response: JobDescription) => {
      dispatch({
        type: RECEIVE_JOB_DESCRIPTION,
        payload: response,
      });
    },
    (error) => {
      const errorMessage: string =
        error?.responseBody?.message || defaultApiErrorMessage;
      dispatch({
        type: RECEIVE_JOB_DESCRIPTION_ERROR,
        payload: errorMessage,
      });
    },
  );
});

export const fetchJobsLocation: (
  agencyId: string,
  programId: string,
) => ThunkAction<void> = flow(
  key(
    (agencyId, programId) =>
      `${formApiBaseUrl(agencyId)}/programs/${programId}/locations`,
  ),
  fetching(),
)((agencyId, programId) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.get(`${formApiBaseUrl(agencyId)}/programs/${programId}/locations`),
  ).then(
    (response: string[]) => {
      dispatch({
        type: RECEIVE_JOB_LOCATIONS,
        payload: response,
      });
    },
    (error) => {
      const errorMessage: string =
        error?.responseBody?.message || defaultApiErrorMessage;
      dispatch({
        type: RECEIVE_JOB_LOCATIONS_ERROR,
        payload: errorMessage,
      });
    },
  );
});

export const getProgramQuestions: (
  agencyId: string,
  programId: string,
) => ThunkAction<void> = flow(
  key(
    (agencyId, programId) =>
      `${formApiBaseUrl(agencyId)}/programs/${programId}/questionnaire`,
  ),
  fetching(),
)((agencyId, programId) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.get(
      `${formApiBaseUrl(agencyId)}/programs/${programId}/questionnaire`,
    ),
  ).then(
    (response: ReferralSelectedQuestionnaire[]) => {
      dispatch({
        type: RECEIVE_PROGRAM_QUESTIONS,
        payload: response,
      });
    },
    (error) => {
      const errorMessage: string =
        error?.responseBody?.message || defaultApiErrorMessage;
      dispatch({
        type: RECEIVE_PROGRAM_QUESTIONS_ERROR,
        payload: errorMessage,
      });
    },
  );
});

export const fetchOptionalResume: () => ThunkAction<boolean> = flow(
  key(() => `filtered/settings`),
  fetching(),
)(() => (dispatch: Dispatch) => {
  return dispatch(reduxApi.get(`filtered/settings`)).then(
    ({
      other_settings: {
        resume_option_for_personalised_form: shouldAllowOptionalResume,
      },
    }: {
      other_settings: {
        resume_option_for_personalised_form: boolean;
      };
    }) => {
      return shouldAllowOptionalResume;
    },
  );
});
